import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HistoryIcon from '@mui/icons-material/History';
import { useAuth } from '../../contexts/AuthContext';
import { shJson } from './api';
import SafetyEvolucaoCharts from './SafetyEvolucaoCharts';
import SafetyQuestionDistribution, {
  NearMissDistributionCharts,
} from './SafetyDistributionCharts';
import SafetyRecurrencePanel from './SafetyRecurrencePanel';

const PERIOD_OPTIONS = [
  { value: 'mes', label: 'Mês atual' },
  { value: 'trimestre', label: 'Trimestre atual' },
  { value: 'ano', label: 'Ano atual' },
  { value: '12', label: 'Últimos 12 meses' },
];

function formatMonthLabel(ym) {
  if (!ym || ym === 'sem-data') return ym;
  const [y, m] = ym.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
}

export default function SafetyHuddleDashboardPage() {
  const { sectorId, templateId } = useParams();
  const navigate = useNavigate();
  const { unidadeSelecionada } = useAuth();

  const [periodPreset, setPeriodPreset] = useState('12');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [drillSubject, setDrillSubject] = useState(null);
  const [drillQuestionId, setDrillQuestionId] = useState(null);

  const buildQuery = useCallback(() => {
    if (periodPreset === '12') return 'months=12';
    return `period=${periodPreset}`;
  }, [periodPreset]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setDrillSubject(null);
    setDrillQuestionId(null);
    try {
      const d = await shJson(
        `/api/safety-huddle/templates/${templateId}/dashboard?${buildQuery()}`,
        unidadeSelecionada
      );
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [templateId, unidadeSelecionada, buildQuery]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const drill = drillSubject
    ? (data?.bySubject || []).find((s) => s.subject === drillSubject)
    : null;

  const drillQuestion = drillQuestionId
    ? Object.values(drill?.questions || {}).find((q) => q.questionId === drillQuestionId)
    : null;

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/safety-huddle/setores/${sectorId}`)}
        sx={{ mb: 2 }}
      >
        Voltar ao setor
      </Button>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} color="primary.main">
            Dashboard — {data?.template?.name}
          </Typography>
          {data?.period?.label && (
            <Typography variant="body2" color="text.secondary">
              Período: {data.period.label}
              {data.period.fromIso && data.period.toIso
                ? ` (${data.period.fromIso} → ${data.period.toIso})`
                : ''}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Período</InputLabel>
            <Select
              label="Período"
              value={periodPreset}
              onChange={(e) => setPeriodPreset(e.target.value)}
            >
              {PERIOD_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() =>
              navigate(`/safety-huddle/setores/${sectorId}/historico/${templateId}`)
            }
          >
            Histórico
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 2 }}>
        <Chip color="primary" label={`${data?.submissionsTotal || 0} envios no período`} />
        <Chip color="warning" label={`${data?.nearMissesTotal || 0} quase-falhas (setor)`} />
        {data?.identifierQuestion && (
          <Chip variant="outlined" label={`ID: ${data.identifierQuestion.label}`} />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <SafetyEvolucaoCharts data={data?.evolucao || []} title="Evolução no período" />
      </Box>

      <Typography variant="h6" fontWeight={700} sx={{ mt: 2, mb: 1 }}>
        Agregação por pergunta
      </Typography>
      {(data?.questions || []).map((q) => (
        <SafetyQuestionDistribution key={q.questionId} question={q} />
      ))}

      <SafetyRecurrencePanel recurrence={data?.recurrence} mode="template" />

      <Typography variant="h6" fontWeight={700} sx={{ mt: 3, mb: 1 }}>
        Quase-falhas do setor
      </Typography>
      <NearMissDistributionCharts
        bySeverity={data?.nearMissBySeverity}
        byStatus={data?.nearMissByStatus}
      />

      {data?.bySubject && (
        <>
          <Typography variant="h6" fontWeight={700} sx={{ mt: 3, mb: 1 }}>
            Cruzamento por identificador
          </Typography>
          <Paper elevation={0} sx={{ overflowX: 'auto', border: '1px solid rgba(25,118,210,0.12)' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Identificador</TableCell>
                  <TableCell align="right">Envios</TableCell>
                  <TableCell>Resumo por pergunta</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.bySubject.map((row) => (
                  <TableRow
                    key={row.subject}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      setDrillSubject(row.subject);
                      setDrillQuestionId(null);
                    }}
                  >
                    <TableCell>
                      <Typography fontWeight={600}>{row.subject}</Typography>
                    </TableCell>
                    <TableCell align="right">{row.submissionCount}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {Object.values(row.questions || {}).map((qq) => (
                          <Chip
                            key={qq.questionId}
                            size="small"
                            variant="outlined"
                            label={`${qq.label}: ${qq.total}`}
                          />
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {drill && (
        <Paper elevation={0} sx={{ p: 2, mt: 2, border: '1px solid rgba(25,118,210,0.2)' }}>
          <Typography fontWeight={700} sx={{ mb: 1 }}>
            Detalhe — {drill.subject}
          </Typography>
          {Object.values(drill.questions || {}).map((qq) => (
            <Box key={qq.questionId} sx={{ mb: 1.5 }}>
              <Button
                size="small"
                variant={drillQuestionId === qq.questionId ? 'contained' : 'text'}
                onClick={() => setDrillQuestionId(qq.questionId)}
                sx={{ textTransform: 'none', fontWeight: 600, mb: 0.5 }}
              >
                {qq.label} ({qq.total} no período)
              </Button>
              {drillQuestionId !== qq.questionId && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', pl: 1 }}>
                  {Object.entries(qq.byValue || {}).map(([k, v]) => (
                    <Chip key={k} size="small" label={`${k}: ${v}`} />
                  ))}
                </Box>
              )}
            </Box>
          ))}

          {drillQuestion && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed rgba(25,118,210,0.2)' }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Evolução mensal — {drillQuestion.label}
              </Typography>
              {drillQuestion.responseType === 'numero' ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Mês</TableCell>
                      <TableCell align="right">Respostas</TableCell>
                      <TableCell align="right">Soma</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(drillQuestion.byMonth || {}).map(([ym, mb]) => (
                      <TableRow key={ym}>
                        <TableCell>{formatMonthLabel(ym)}</TableCell>
                        <TableCell align="right">{mb.total}</TableCell>
                        <TableCell align="right">{mb.numberSum ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Mês</TableCell>
                      <TableCell>Distribuição</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(drillQuestion.byMonth || {}).map(([ym, mb]) => (
                      <TableRow key={ym}>
                        <TableCell>{formatMonthLabel(ym)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {Object.entries(mb.byValue || {}).map(([k, v]) => (
                              <Chip key={k} size="small" label={`${k}: ${v}`} />
                            ))}
                            {!Object.keys(mb.byValue || {}).length && mb.total > 0 && (
                              <Typography variant="caption">{mb.total} registro(s)</Typography>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          )}

          <Button
            size="small"
            sx={{ mt: 1 }}
            onClick={() => {
              setDrillSubject(null);
              setDrillQuestionId(null);
            }}
          >
            Fechar
          </Button>
        </Paper>
      )}

      <Typography variant="h6" fontWeight={700} sx={{ mt: 3, mb: 1 }}>
        Últimos envios no período
      </Typography>
      {!data?.recentSubmissions?.length ? (
        <Alert severity="info">Sem envios neste período.</Alert>
      ) : (
        data.recentSubmissions.map((s) => (
          <Typography key={s.id} variant="body2" sx={{ mb: 0.5 }}>
            #{s.id}
            {s.subjectLabel ? ` · ${s.subjectLabel}` : ''} · {s.createdByNome || '—'} ·{' '}
            {s.createdAt}
            {s.referenceDate ? ` · ref ${s.referenceDate}` : ''}
          </Typography>
        ))
      )}
    </Box>
  );
}
